import React from "react";
import { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Webcam, ArrowBigRight, Factory, PenIcon, SquarePen, Trash, ClipboardList, Lock, Unlock } from "lucide-react";
import { getCustomerWithCamaras, getWeeklyStats, customerSatisfactionScore, updateCustomerVisibility, deleteCustomer } from '../api/AdminDashboard.api';
import { checkCameraActive } from '../../camera/api/camStatus.api';
import { getCustomerPaymentType } from '../api/payments.api';
import { getCustomerCountry } from '../utils/countryFromPhone.js';
import { getCustomerState } from '../utils/provinceFromPhone.js';
import CustomerLogsModal from "./CustomerLogsModal.jsx";
import CustomerPaymentsModal from "./CustomerPaymentsModal.jsx";
import SidebarConfigModal from "./SidebarConfigModal.jsx";
import CustomerDetailCard from "./CustomerDetailsCard.jsx";
import CamerasTableCard from "./CamarasTableCard.jsx";
import DeleteCustomerModal from "./DeleteCustomerModal.jsx";

import SaveOrEditCamaras from "./SaveOrEditCamaras.tsx";
import SaveOrEditCustomer from "./SaveOrEditCustomer.tsx";
import FtpUserComponent from "./FtpUser.tsx";
import PendingCamaraFormModal from "../../pendingCamaras/components/PendingCamaraFormModal.tsx";

const DAYS_FOR_RED_INDICATOR_COLOR = 30
const DAYS_FOR_YELLOW_INDICATOR_COLOR = 60;
const PWD_TO_DELETE = "lisualborrar";
const CAMERA_CHECK_CONCURRENCY = 6;

const getColorClass = (days) => {
  if (days < DAYS_FOR_RED_INDICATOR_COLOR) { return 'red' }
  if (days >= DAYS_FOR_RED_INDICATOR_COLOR && days <= DAYS_FOR_YELLOW_INDICATOR_COLOR) { return 'yellow' }
  return 'green';
};

const getIndicatorColor = (camaras) => {
  if (!camaras || camaras.length === 0) {
    return 'bg-gray-400';
  }
  if (camaras.some(camara => getColorClass(camara.feedDays?.daysLeft) === 'red')) {
    return 'bg-red-500';
  }
  if (camaras.some(camara => getColorClass(camara.feedDays?.daysLeft) === 'yellow')) {
    return 'bg-yellow-500';
  }
  if (camaras.some(camara => getColorClass(camara.feedDays?.daysLeft) === 'green')) {
    return 'bg-green-500';
  }
  return 'bg-gray-400';
};

Date.prototype.getWeek = function () {
  const date = new Date(this.getTime());
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
  const week1 = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
};

const AdminDashboard = () => {

  const [showCamaras, setShowCamaras] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customerWithCamaras, setCustomerWithCamaras] = useState([]);
  const [error, setError] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isCamaraModalOpen, setIsCamaraModalOpen] = useState(false);
  const [isFtpModalOpen, setIsFtpModalOpen] = useState(false);
  const [isAddCamaraModalOpen, setIsAddCamaraModalOpen] = useState(false);
  const [addCamaraCustomerId, setAddCamaraCustomerId] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCamara, setSelectedCamara] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("active");
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortField, setSortField] = useState("createdAt");
  const [auditLogs, setAuditLogs] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedLogsForModal, setSelectedLogsForModal] = useState(null);
  const [selectedCustomerForPayments, setSelectedCustomerForPayments] = useState(null);
  const [selectedCustomerForSidebarConfig, setSelectedCustomerForSidebarConfig] = useState(null);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [imageStatusByCustomer, setImageStatusByCustomer] = useState({});
  const [checkingAllCameras, setCheckingAllCameras] = useState(false);
  const [paymentTypeByCustomer, setPaymentTypeByCustomer] = useState({});
  const [checkingAllLicenses, setCheckingAllLicenses] = useState(false);
  const [licensesChecked, setLicensesChecked] = useState(false);

  const fetchCustomerWithCamaras = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCustomerWithCamaras();
      setCustomerWithCamaras(data);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async (date) => {
    const year = date.getFullYear();
    const week = date.getWeek();
    try {
      const data = await getWeeklyStats(year, week);
      setAuditLogs(data || []);
    } catch (error) {
      console.error("Error fetching audit logs:", error);
      setAuditLogs([]);
    }
  };

  useEffect(() => {
    fetchCustomerWithCamaras();
  }, []);

  useEffect(() => {
    fetchAuditLogs(selectedDate);
  }, [selectedDate]);

  const handleDateChange = (e) => {
    const newDate = new Date(e.target.value);
    if (!isNaN(newDate)) {
      setSelectedDate(newDate);
    }
  };

  const handleShowCustomerInfo = (customerId) => {
    if (selectedCustomerId === customerId) {
      setSelectedCustomerId(null);
    } else {
      setSelectedCustomerId(customerId);
    }
  };

  const handleSaveOrEditCustomer = (customer) => {
    setSelectedCustomer(customer);
    setIsCustomerModalOpen(true);
  };

  const closeCustomerModal = () => {
    setIsCustomerModalOpen(false);
    setSelectedCustomer(null);
    fetchCustomerWithCamaras();
  };

  const closeCamaraModal = () => {
    setIsCamaraModalOpen(false);
    setSelectedCamara(null);
    fetchCustomerWithCamaras();
  };

  const closeFtpModal = () => {
    setIsFtpModalOpen(false);
    setSelectedCamara(null);
    fetchCustomerWithCamaras();
  };

  const closeCamaraModalCancel = () => {
    setIsCamaraModalOpen(false);
    setSelectedCamara(null);
  };

  const closeCustomerModalCancel = () => {
    setIsCustomerModalOpen(false);
    setSelectedCustomer(null);
  };

  const closeFtpModalCancel = () => {
    setIsFtpModalOpen(false);
    setSelectedCamara(null);
  };

const handleSatisfaction = async (customerId, score) => {
  try {
    await customerSatisfactionScore(customerId, score);
    setCustomerWithCamaras(prevCustomers => 
      prevCustomers.map(cust => 
        cust.id === customerId || cust.customerId === customerId 
          ? { ...cust, satisfactionScore: score } 
          : cust
      )
    );
    toast.success(`Puntuación de ${score} registrada`);
  } catch (error) {
    console.error(error);
    toast.error("Error al registrar");
  }
};

const handleToggleVisibleForEditor = async (customer) => {
  const newValue = !customer.visibleForEditor;
  try {
    await updateCustomerVisibility(customer.id, newValue);
    setCustomerWithCamaras(prevCustomers =>
      prevCustomers.map(cust =>
        cust.id === customer.id ? { ...cust, visibleForEditor: newValue } : cust
      )
    );
    toast.success(newValue ? "Cliente visible para el editor" : "Cliente oculto para el editor");
  } catch (error) {
    console.error(error);
    toast.error("Error al actualizar visibilidad del cliente");
  }
};

const handleDeleteRequest = (customer) => {
  setCustomerToDelete(customer);
};

const handleConfirmDelete = async (password) => {
  if (password !== PWD_TO_DELETE) {
    toast.error("Contraseña incorrecta.");
    return;
  }
  if (!customerToDelete) return;
  try {
    await deleteCustomer(customerToDelete.id);
    toast.success(`Cliente ${customerToDelete.name} eliminado correctamente.`);
    setCustomerToDelete(null);
    fetchCustomerWithCamaras();
  } catch (error) {
    console.error("Error deleting customer:", error);
    toast.error("Error al eliminar el cliente.");
    setCustomerToDelete(null);
  }
};

const handleCheckAllCamerasStatus = async () => {
  const cameraChecks = customerWithCamaras.flatMap((customer) =>
    (customer.camaras || []).map((camara) => ({ customerId: customer.id, feedID: camara.feedID }))
  );

  if (cameraChecks.length === 0) {
    toast.info("No hay cámaras para verificar.");
    return;
  }

  setCheckingAllCameras(true);
  const failedCustomerIds = new Set();
  let cursor = 0;

  const worker = async () => {
    while (cursor < cameraChecks.length) {
      const { customerId, feedID } = cameraChecks[cursor++];
      const isActive = await checkCameraActive(feedID);
      if (!isActive) {
        failedCustomerIds.add(customerId);
      }
    }
  };

  try {
    await Promise.all(
      Array.from({ length: Math.min(CAMERA_CHECK_CONCURRENCY, cameraChecks.length) }, worker)
    );

    const nextStatus = {};
    customerWithCamaras.forEach((customer) => {
      nextStatus[customer.id] = failedCustomerIds.has(customer.id) ? 'alert' : 'ok';
    });
    setImageStatusByCustomer(nextStatus);
    toast.success(`Verificación completa: ${cameraChecks.length} cámaras revisadas.`);
  } finally {
    setCheckingAllCameras(false);
  }
};

const handleCheckAllLicenses = async () => {
  if (customerWithCamaras.length === 0) {
    toast.info("No hay clientes para verificar.");
    return;
  }

  setCheckingAllLicenses(true);
  const nextPaymentType = {};
  let cursor = 0;

  const worker = async () => {
    while (cursor < customerWithCamaras.length) {
      const customer = customerWithCamaras[cursor++];
      try {
        const result = await getCustomerPaymentType(customer.customerId);
        nextPaymentType[customer.customerId] = result?.type || null;
      } catch (err) {
        nextPaymentType[customer.customerId] = null;
      }
    }
  };

  try {
    await Promise.all(
      Array.from({ length: Math.min(CAMERA_CHECK_CONCURRENCY, customerWithCamaras.length) }, worker)
    );
    setPaymentTypeByCustomer(nextPaymentType);
    setLicensesChecked(true);
    toast.success(`Verificación completa: ${customerWithCamaras.length} clientes revisados.`);
  } finally {
    setCheckingAllLicenses(false);
  }
};

const inactiveCustomersCount = customerWithCamaras.filter((customer) => !customer.active).length;

const filteredAndSortedCustomers = customerWithCamaras
    .filter((customer) => activeTab === "active" ? customer.active : !customer.active)
    .filter((customer) => {
      const searchLower = searchTerm.toLowerCase();
      const nameMatch = customer.name?.toLowerCase().includes(searchLower);
      const identifierMatch = customer.identifier?.toLowerCase().includes(searchLower);
      const idMatch = customer.customerId?.toLowerCase().includes(searchLower);
      const emailMatch = customer.mainUserEmail?.toLowerCase().includes(searchLower);
      const userNameMatch = customer.mainUserName?.toLowerCase().includes(searchLower);
      const camaraMatch = customer.camaras?.some(cam =>
        cam.camaraUID?.toLowerCase().includes(searchLower) ||
        cam.feedID?.toLowerCase().includes(searchLower) ||
        cam.identifier?.toLowerCase().includes(searchLower) ||
        cam.chipICCID?.toLowerCase().includes(searchLower)
      );
      return nameMatch || identifierMatch || idMatch || emailMatch || userNameMatch || camaraMatch;
    })
    .sort((a, b) => {
      if (sortField === "createdAt") {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
      } else if (sortField === "name") {
        const nameA = a.name?.toLowerCase() || "";
        const nameB = b.name?.toLowerCase() || "";
        if (nameA < nameB) return sortOrder === "asc" ? -1 : 1;
        if (nameA > nameB) return sortOrder === "asc" ? 1 : -1;
        return 0;
      }
      return 0;
    });

  const getLoginCount = (customerId) => {
    return auditLogs.filter(log => log.customerId === customerId).length;
  };

  const openLogs = (customer) => {
    const logs = auditLogs.filter(log => log.customerId === customer.customerId);
    setSelectedLogsForModal({
      name: customer.name,
      logs: logs
    });
  };

  const openPayments = (customer) => {
    setSelectedCustomerForPayments({
      customerId: customer.customerId,
      name: customer.name
    });
  };

  const openSidebarConfig = (customer) => {
    setSelectedCustomerForSidebarConfig({
      customerId: customer.customerId,
      name: customer.name
    });
  };

return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">
            Información de Clientes / Camaras
        </h1>
        <div className="flex items-center gap-3 bg-white p-2 rounded-lg shadow-sm border border-gray-200">
          <label className="text-xs font-bold text-gray-500 uppercase">Ingresos/semana:</label>
          <input
            type="week"
            onChange={(e) => {
              const [y, w] = e.target.value.split("-W");
              if (y && w) {
                const d = new Date(y, 0, 1 + (w - 1) * 7);
                setSelectedDate(d);
              }
            }}
            className="text-sm font-semibold text-blue-600 outline-none cursor-pointer"
          />
          <span className="text-xs text-gray-400">
            Año: {selectedDate.getFullYear()} - Sem: {selectedDate.getWeek()}
          </span>
        </div>
      </div>

      <div className="mb-4 flex border-b border-gray-200">
        <button
          type="button"
          onClick={() => setActiveTab("active")}
          className={`min-h-[44px] lg:min-h-0 px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
            activeTab === "active"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          Activos
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("inactive")}
          className={`min-h-[44px] lg:min-h-0 px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
            activeTab === "inactive"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          Inactivos {inactiveCustomersCount > 0 && `(${inactiveCustomersCount})`}
        </button>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          placeholder="Buscar por nombre, ID, email, usuario, UID, Cod, FeedID, Chip ICCID"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
        />
        <button
          type="button"
          onClick={handleCheckAllCamerasStatus}
          disabled={checkingAllCameras}
          className="min-h-[44px] lg:min-h-0 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
        >
          {checkingAllCameras ? "Verificando cámaras..." : "Verificar estado de todas las cámaras"}
        </button>
        <button
          type="button"
          onClick={handleCheckAllLicenses}
          disabled={checkingAllLicenses}
          className="min-h-[44px] lg:min-h-0 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
        >
          {checkingAllLicenses ? "Verificando licencias..." : "Verificar todas las licencias"}
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      ) : customerWithCamaras.length === 0 ? (
        <div className="text-center py-10 text-gray-500">
          Información de Clientes no disponible.
        </div>
      ) : (
        <div className="overflow-x-auto bg-white shadow-md rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-blue-200">
              <tr>
                <th
                  className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-blue-300 transition-colors select-none"
                  onClick={() => {
                    if (sortField === "name") {
                      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                    } else {
                      setSortField("name");
                      setSortOrder("asc");
                    }
                  }}
                >
                  Cliente {sortField === "name" ? (sortOrder === "asc" ? "↑" : "↓") : ""}
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Usuario Principal
                </th>
                <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                  País
                </th>
                <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Provincia
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Activo
                </th>
                <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Editor
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-blue-300 transition-colors select-none"
                  onClick={() => {
                    if (sortField === "createdAt") {
                      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                    } else {
                      setSortField("createdAt");
                      setSortOrder("desc");
                    }
                  }}
                >
                  Alta {sortField === "createdAt" ? (sortOrder === "asc" ? "↑" : "↓") : ""}
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Cams
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Licencias
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Imágenes
                </th>
                <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                  LogIn
                </th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAndSortedCustomers.map((customer) => (
                <React.Fragment key={customer.id}>
                  <tr
                    onClick={() => handleShowCustomerInfo(customer.id)}
                    className={`cursor-pointer transition-colors ${selectedCustomerId === customer.id
                      ? "bg-gray-200 dark:bg-gray-200 border-l-4 border-gray-400"
                      : "hover:bg-gray-100 dark:hover:bg-gray-200"
                      }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-bold">{customer.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                      {customer.mainUserName || <span className="text-gray-400 italic">No asignado</span>}
                    </td>
                    <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-bold">
                      {getCustomerCountry(customer) || <span className="text-gray-400 italic">Sin datos</span>}
                    </td>
                    <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-bold">
                      {getCustomerState(customer) || <span className="text-gray-400 italic">Sin datos</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-bold">
                      <div className={`w-4 h-4 rounded-full ${customer.active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    </td>
                    <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-bold">
                      <span
                        className="inline-flex p-1.5"
                        title={customer.visibleForEditor ? "Visible para editor" : "Oculto para editor"}
                      >
                        {customer.visibleForEditor ? (
                          <Unlock className="w-4 h-4 text-green-600" />
                        ) : (
                          <Lock className="w-4 h-4 text-red-500" />
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-bold">
                      {new Date(customer.createdAt).toLocaleDateString('es-ES', {
                        day: '2-digit',
                        month: '2-digit',
                        year: '2-digit',
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-bold">{customer.camaras.length}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-bold">
                      <div className="flex items-center gap-2">
                        {!licensesChecked ? (
                          customer.camaras.length > 0 && (
                            <div className="w-5 h-5 rounded-full bg-gray-300" title="Sin verificar"></div>
                          )
                        ) : paymentTypeByCustomer[customer.customerId] === 'mensual' ? (
                          <div
                            className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center text-white text-[10px] font-bold"
                            title="Mensual (MRR)"
                          >
                            M
                          </div>
                        ) : (
                          customer.camaras.length > 0 && (
                            <div
                              className={`w-5 h-5 rounded-full ${getIndicatorColor(customer.camaras)} flex items-center justify-center text-white text-[10px] font-bold`}
                              title="Paquete de días"
                            >
                              D
                            </div>
                          )
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-bold">
                      <div className="flex items-center gap-2" title={
                        imageStatusByCustomer[customer.id] === 'alert'
                          ? 'Hay cámaras sin enviar imágenes correctamente'
                          : imageStatusByCustomer[customer.id] === 'ok'
                            ? 'Todas las cámaras enviaron imágenes correctamente'
                            : 'Sin verificar'
                      }>
                        {customer.camaras.length > 0 && (
                          <div className={`w-3 h-3 rounded-full ${
                            imageStatusByCustomer[customer.id] === 'alert'
                              ? 'bg-red-500'
                              : imageStatusByCustomer[customer.id] === 'ok'
                                ? 'bg-green-500'
                                : 'bg-gray-300'
                          }`}></div>
                        )}
                      </div>
                    </td>
                    <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-bold text-center">
                      <span className="bg-blue-50 px-2 py-1 rounded border border-blue-100">
                        {getLoginCount(customer.customerId)}
                      </span>
                    </td>
                  </tr>

                  {selectedCustomerId === customer.id && (
                    <>
                      <tr>
                        <td colSpan={11} className="p-4">
                          <CustomerDetailCard 
                            customer={customer}
                            loginCount={getLoginCount(customer.customerId)}
                            onOpenLogs={openLogs}
                            onOpenPayments={openPayments}
                            onOpenSidebarConfig={openSidebarConfig}
                            onEdit={handleSaveOrEditCustomer}
                            onDashboard={() => window.open(`/customerDashboard/${customer.customerId}`, '_blank')}
                            onSatisfaction={handleSatisfaction}
                            onToggleVisibleForEditor={handleToggleVisibleForEditor}
                            onDelete={handleDeleteRequest}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={11} className="p-4">
                          <CamerasTableCard
                            camaras={customer.camaras}
                            customerId={customer.customerId}
                            getColorClass={getColorClass}
                            onEditCamara={(camara) => {
                              setSelectedCamara(camara);
                              setIsCamaraModalOpen(true);
                            }}
                            onOpenFtpModal={(camara) => {
                              setSelectedCamara(camara);
                              setIsFtpModalOpen(true);
                            }}
                            onOpenAddCamaraModal={() => {
                              setAddCamaraCustomerId(customer.customerId);
                              setIsAddCamaraModalOpen(true);
                            }}
                            onRefresh={fetchCustomerWithCamaras}
                          />
                        </td>
                      </tr>
                    </>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isCamaraModalOpen && (
        <SaveOrEditCamaras
          isOpen={isCamaraModalOpen} 
          onClose={closeCamaraModal}
          onCloseCancel={closeCamaraModalCancel}
          camara={selectedCamara ?? undefined}
        />
      )}

      {isFtpModalOpen && (
        <FtpUserComponent
          isOpen={isFtpModalOpen}
          onClose={closeFtpModal}
          onCloseCancel={closeFtpModalCancel}
          customerId={selectedCamara?.customerId}
          camIdentifier={selectedCamara?.identifier}
        />
      )}

      <PendingCamaraFormModal
        isOpen={isAddCamaraModalOpen}
        onClose={() => setIsAddCamaraModalOpen(false)}
        initialCustomerId={addCamaraCustomerId}
        onSaved={fetchCustomerWithCamaras}
      />

      {isCustomerModalOpen && (
        <SaveOrEditCustomer
          isOpen={isCustomerModalOpen}
          onClose={closeCustomerModal}
          onCloseCancel={closeCustomerModalCancel}
          customer={selectedCustomer ?? undefined}
        />
      )}

      <ToastContainer />

      <CustomerLogsModal
        isOpen={!!selectedLogsForModal}
        onClose={() => setSelectedLogsForModal(null)}
        customerName={selectedLogsForModal?.name}
        logs={selectedLogsForModal?.logs}
      />

      <CustomerPaymentsModal
        isOpen={!!selectedCustomerForPayments}
        onClose={() => setSelectedCustomerForPayments(null)}
        customerId={selectedCustomerForPayments?.customerId}
        customerName={selectedCustomerForPayments?.name}
      />

      <SidebarConfigModal
        isOpen={!!selectedCustomerForSidebarConfig}
        onClose={() => setSelectedCustomerForSidebarConfig(null)}
        customerId={selectedCustomerForSidebarConfig?.customerId}
        customerName={selectedCustomerForSidebarConfig?.name}
      />

      {customerToDelete && (
        <DeleteCustomerModal
          isOpen={!!customerToDelete}
          onClose={() => setCustomerToDelete(null)}
          onConfirm={handleConfirmDelete}
          customerName={customerToDelete.name}
        />
      )}
    </div>
  );
}

export default AdminDashboard
